import React, {useMemo, useState} from 'react';
import {Button} from '@mantine/core';
import {IconUpload} from '@tabler/icons-react';
import type {CourseVideoRequest} from 'src/api/ProactApiClient';
import {ContentModal} from 'src/design/molecules/ContentModal';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import {useCreateCourseVideoMutation} from 'src/redux/queries/courseVideo';
import {useAlert} from 'src/shared/hooks/useAlert';
import type {IFormFieldV2} from 'src/types/FormV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

const uploadFormMeta: IFormFieldV2[] = [
  {
    name: 'title',
    label: 'Title',
    type: 'text',
    required: true,
    size: 'xl',
    placeholder: 'My Uploaded Video',
    description: 'Shown in the Course Videos library for reuse in other lessons.',
  },
  {
    name: 'contentUrl',
    label: 'Video File',
    type: 'file',
    required: true,
    size: 'xl',
    fileUploadOptions: {
      allowMultiple: false,
      maxFileSize: 300e6,
      allowedFileTypes: ['video/*'],
      simplifiedUploader: true,
    },
  },
];

export interface InlineVideoUploadProps {
  /** Called with the newly created video's id so the lesson can auto-select it. */
  onUploaded: (videoId: number) => void;
}

/**
 * Compact "upload a new video without leaving the page" control for the
 * lesson editor, reusing the Course Videos create mutation instead of
 * forcing a detour through the separate Course Videos module. On success
 * the caller auto-selects the new video — no unsaved draft is lost since
 * this only opens a modal over the still-mounted lesson editor.
 */
export const InlineVideoUpload: React.FC<InlineVideoUploadProps> = ({
  onUploaded,
}) => {
  const {setAlert} = useAlert();
  const [opened, setOpened] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const formMeta = useMemo(() => uploadFormMeta, []);
  const form = useProactForm({formMeta});
  const [createCourseVideo] = useCreateCourseVideoMutation();

  const handleClose = () => {
    setOpened(false);
    form.resetForm();
  };

  const handleUpload = async () => {
    if (!(await form.validateForm())) {
      return;
    }

    try {
      setIsSaving(true);
      const values = form.getFieldValues();
      const body = {
        title: values.title,
        contentUrl: values.contentUrl,
        active: true,
      } as CourseVideoRequest;

      const response = await createCourseVideo(body).unwrap();

      setAlert(`Video "${response.title}" was successfully uploaded.`, 'success');
      onUploaded(response.id!);
      handleClose();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button
        variant="subtle"
        size="sm"
        leftSection={<IconUpload size={16} />}
        onClick={() => setOpened(true)}>
        Upload new video
      </Button>

      <ContentModal
        title="Upload Video"
        open={opened}
        onCloseCallback={handleClose}
        actions={[
          <SaveButton key="save" loading={isSaving} onClick={handleUpload}>
            Upload &amp; Select
          </SaveButton>,
        ]}>
        <ProactForm form={form}>
          <ProactFormGrid />
        </ProactForm>
      </ContentModal>
    </>
  );
};
